/* eslint-disable jsx-a11y/anchor-is-valid */
import './Antragsuebersicht.css';
import React, { CSSProperties, useState } from 'react';
import {
  Button,
  Datagrid,
  Input,
  Select,
  Stack,
  Text,
  Title
} from '@efast_public/fjd-component-library';
import {
  bearbeitungsstandSelectOptions,
  bescheidstatusSelectOptions,
  buildLikeQuery,
  formatByte,
  SelectOption,
  useBearbeiterSelectOption
} from '../../shared/utils/antragsuebersicht';
import { resolvePaginatedPath } from '../../shared/utils/pagination-utils';
import { AntragObj } from '../../api/models/Antrag/AntragsModel';
import useLocalStorage from 'use-local-storage';
import useFilterBadges from '../../shared/hooks/useFilterBadges';
import { useLocalStorageTable } from '../../shared/hooks/useLocalStorageTable';
import { useDebounceCallback } from '@react-hook/debounce';
import useTabs from '../../shared/hooks/useTabs';
import {
  bearbeitungsstandCell,
  bescheidstatusCell,
  rueckkanalCell,
  statusCell
} from './AntragsuebersichtCustomizedCell';
import _moment from 'moment/moment';
import ZuweisungComponent from '../shared/Zuweisung/ZuweisungComponent';
import { EmptyStateComponent } from '../shared/EmptyStateComponent';
import { ResultsObject } from '../../api/models/ResultsObject/ResultsObject';
import { useTranslation } from 'react-i18next';
import { Bearbeitungsstand } from '../../api/models/Status/Bearbeitungsstand';
import { useQuery } from '@tanstack/react-query';
import { BehApiRoutes } from '../../api/beh-api';
import { useProfile } from 'shared/hooks/AuthContext';

const msgNoCases: string =
  'Um Anträge sehen und bearbeiten zu können, müssen Sie mit der Rolle “Sachbearbeitung” einer Gruppe zugeordnet sein.';

const localSotrageKeyBearbeitungsstandFilter: string = 'Bearbeitungsstand';
const localSotrageKeyBescheidsstatusFilter: string = 'Bescheidstatus';
const localSotrageKeyBearbeiterFilter: string = 'Bearbeiter';

const textStyleAbgeschlossen = (
  bearbeitungsstand: Bearbeitungsstand
): CSSProperties => {
  return bearbeitungsstand === 'ABGESCHLOSSEN'
    ? { color: 'var(--fjd-color-text-dark-muted)' }
    : {};
};

export function AntragsuebersichtPage() {
  const { t } = useTranslation();
  const { openTab } = useTabs();
  const { currentPageKey, setLocalStorageItem } = useLocalStorageTable();

  const [selectedRows, setSelectedRows] = useState<Array<AntragObj>>([]);
  const [paginationState, setPaginationState] = useState<{
    pageIndex: number;
    pageSize: number;
  }>({
    pageSize: 10,
    pageIndex: 0
  });
  const [tableSortOrder, setTableSortOrder] = useState<{
    id?: string;
    direction: 'ASC' | 'DESC' | undefined;
  }>({ id: 'createdOn', direction: 'DESC' });
  const [bearbeitungsstandFilter, setBearbeitungsstandFilter] = useLocalStorage(
    'Bearbeitungsstand',
    ''
  );
  const [bescheidstatusFilter, setBescheidstatusFilter] = useLocalStorage(
    'Bescheidstatus',
    ''
  );
  const [bearbeiterFilter, setBearbeiterFilter] = useLocalStorage<
    Array<SelectOption>
  >('Bearbeiter', []);
  const [columnVisibility, setColumnVisibility] = useLocalStorage<
    Record<string, boolean>
  >('antragColumnVisibility', {
    organizationalUnitName: false,
    antragsArt: false,
    absenderName: false,
    bescheidstatus: false,
    bearbeitungsstand: false
  });

  const [searchAntraege, setSearchAntraege] = useState<string>();
  const [inputValue, setInputValue] = useState('');

  const { hasRole } = useProfile();
  const hasSachbearbeitungRole = hasRole('Sachbearbeitung');
  const dropdownFilterEmpty =
    bearbeitungsstandFilter?.length === 0 &&
    bescheidstatusFilter?.length === 0 &&
    bearbeiterFilter?.length === 0;
  const noFilterSelected = dropdownFilterEmpty && inputValue === '';

  const debouncedSetSearchTerm = useDebounceCallback(
    setSearchAntraege,
    1000,
    false
  );

  const { bearbeiterSelectOptions } = useBearbeiterSelectOption(); // No error handling needed, because here errors are catched via route

  const { registerFilter, removeBadge, filterBadges, resetFilterBadges } =
    useFilterBadges();

  const onUpdateBearbeitungsstand = registerFilter(
    localSotrageKeyBearbeitungsstandFilter,
    bearbeitungsstandSelectOptions,
    setBearbeitungsstandFilter
  );

  const updateBearbeitungsstandBadges = (selectedValue: Array<string>) => {
    onUpdateBearbeitungsstand(
      selectedValue,
      bearbeitungsstandFilter?.split('_') ?? []
    );
  };

  const onUpdateZuweisung = registerFilter(
    localSotrageKeyBearbeiterFilter,
    bearbeiterSelectOptions,
    setBearbeiterFilter
  );

  const updateZuweisungFilterBadges = (selectedValue: Array<SelectOption>) => {
    onUpdateZuweisung(
      selectedValue.map((v: SelectOption) => v.value),
      bearbeiterFilter ? bearbeiterFilter.map((v: SelectOption) => v.value) : []
    );
  };

  const onUpdateBescheidstatus = registerFilter(
    localSotrageKeyBescheidsstatusFilter,
    bescheidstatusSelectOptions,
    setBescheidstatusFilter
  );

  const updateBescheidstatusBadges = (selectedValue: Array<string>) => {
    onUpdateBescheidstatus(
      selectedValue,
      bescheidstatusFilter?.split('_') ?? []
    );
  };

  const clearFilter = () => {
    setLocalStorageItem(currentPageKey, '0');
    setBearbeiterFilter([]);
    setBescheidstatusFilter('');
    setBearbeitungsstandFilter('');
    setInputValue('');
    setSearchAntraege('');
    resetFilterBadges();
  };

  const openSelection = () => {
    if (selectedRows.length > 0) {
      selectedRows.forEach((element, index) => {
        const isLast = index === selectedRows.length - 1;
        openTab(element, isLast);
      });
    }
  };

  const { data, isLoading } = useQuery<ResultsObject<AntragObj>>({
    queryKey: [
      resolvePaginatedPath({
        key: hasSachbearbeitungRole ? BehApiRoutes.allCases() : null,
        sortByOrder: tableSortOrder.direction,
        filter: [
          {
            property: 'bearbeitungsstand',
            operator: 'eq',
            value: bearbeitungsstandFilter
          },
          {
            property: 'bescheidstatus',
            operator: 'eq',
            value: bescheidstatusFilter
          },
          {
            property: 'userEntity.userId',
            operator: 'eq',
            value: bearbeiterFilter.at(0)?.value
          },
          {
            property: 'name$auftragsnummer',
            operator: 'like',
            value: buildLikeQuery(searchAntraege)
          }
        ],
        sortByProperty: tableSortOrder.id,
        initPageSize: paginationState.pageSize,
        page: paginationState.pageIndex
      })
    ],
    enabled: !!hasSachbearbeitungRole
  });

  if (!hasSachbearbeitungRole) {
    return <EmptyStateComponent msg={msgNoCases} />;
  }

  return (
    <Stack spacing="xl">
      <Title level={1} visualLevel={2}>Antragsübersicht</Title>
      <Stack spacing="s">
        <Input
          type="search"
          onChange={(event) => {
            debouncedSetSearchTerm(event.target.value);
            setInputValue(event.target.value);
          }}
          id="antraege_durchsuchen"
          data-testid={'antraege_durchsuchen'}
          placeholder={t('antragsuebersicht.searchInput.placeholder')}
          maxLength={50}
          value={inputValue}
        />
        <Stack direction="row" spacing="m">
          <Select
            selectType="multi"
            id="bearbeitungsstand_filter"
            label={'Bearbeitungsstand'}
            placeholder="Bearbeitungsstand"
            selectedOptions={
              bearbeitungsstandFilter === ''
                ? []
                : bearbeitungsstandFilter
                    ?.split('_')
                    .map((v) => ({ value: v, label: v }))
            }
            onChange={(selectedValue) => {
              const values = selectedValue.map((v) => v.value);
              setLocalStorageItem(currentPageKey, '0');
              setBearbeitungsstandFilter(() => values.join('_'));
              updateBearbeitungsstandBadges(values);
            }}
            options={bearbeitungsstandSelectOptions.map(
              (bearbeitungsstand) => ({
                disabled: false,
                label: bearbeitungsstand.label,
                value: bearbeitungsstand.value
              })
            )}
          />
          <Select
            selectType="multi"
            id="bescheidstatus_filter"
            label={'Bescheidstatus'}
            placeholder="Bescheidstatus"
            selectedOptions={
              bescheidstatusFilter === ''
                ? []
                : bescheidstatusFilter
                    ?.split('_')
                    .map((v) => ({ value: v, label: v }))
            }
            onChange={(selectedValue) => {
              const values = selectedValue.map((v) => v.value);
              setLocalStorageItem(currentPageKey, '0');
              setBescheidstatusFilter(() => values.join('_'));
              updateBescheidstatusBadges(values);
            }}
            options={bescheidstatusSelectOptions.map(
              (bescheid: SelectOption) => ({
                disabled: false,
                label: bescheid.label,
                value: bescheid.value
              })
            )}
          />
          <Select
            id="zuweisung_filter"
            placeholder={'Zuweisung'}
            selectedOptions={bearbeiterFilter}
            onChange={(selectedValue) => {
              setLocalStorageItem(currentPageKey, '0');
              setBearbeiterFilter(selectedValue);
              updateZuweisungFilterBadges(selectedValue);
            }}
            options={bearbeiterSelectOptions.map(
              (bearbeiter: SelectOption) => ({
                disabled: false,
                label: bearbeiter.label,
                value: bearbeiter.value
              })
            )}
          />
          {!noFilterSelected ? (
            <Button
              variant="primary-text"
              onClick={clearFilter}
              id="filter_zuruecksetzen"
              data-testid={'filter_zuruecksetzen'}
            >
              Filter zurücksetzen
            </Button>
          ) : null}
        </Stack>
        <Stack id={'stack'} spacing="s">
          <Datagrid<AntragObj>
            columns={[
              {
                id: 'status',
                header: 'Status',
                accessorKey: 'bescheidstatus',
                enableHiding: true,
                size: 70,
                minSize: 2,
                enableSorting: false,
                cell: (row) => (
                  <div
                    style={{
                      paddingLeft: 'var(--fjd2-spacing-xs)'
                    }}
                  >
                    {statusCell(
                      row.row.original.bearbeitungsstand,
                      row.row.original.bescheidstatus,
                      row.row.original.bescheidUeberfaellig
                    )}
                  </div>
                )
              },
              {
                id: 'createdOn',
                header: 'Datum',
                accessorKey: 'createdOn',
                enableHiding: true,
                size: 200,
                minSize: 4,
                enableSorting: true,
                enableResizing: true,
                sortingFn: 'datetime',
                cell: (row) => (
                  <a
                    role="button"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      openTab(row.row.original);
                    }}
                  >
                    <Text
                      variant="primary"
                      strong={row.row.original.bearbeitungsstand === 'OFFEN'}
                    >
                      {_moment
                        .utc(row.getValue<string>())
                        .local()
                        .format('DD.MM.YYYY HH:mm')}
                    </Text>
                  </a>
                )
              },
              {
                id: 'name',
                header: 'Antragstitel',
                accessorKey: 'name',
                enableHiding: true,
                size: 500,
                minSize: 4,
                enableSorting: true,
                sortingFn: 'alphanumeric',
                enableResizing: true,
                cell: (row) => (
                  <a
                    role="button"
                    style={{ cursor: 'pointer', height: '62px' }}
                    onClick={() => {
                      openTab(row.row.original);
                    }}
                  >
                    <Text
                      variant="primary"
                      strong={row.row.original.bearbeitungsstand === 'OFFEN'}
                    >
                      {row.getValue<string>()}
                    </Text>
                  </a>
                )
              },
              {
                id: 'auftragsnummer',
                header: 'Auftragsnummer',
                accessorKey: 'auftragsnummer',
                enableHiding: true,
                size: 200,
                minSize: 4,
                enableSorting: true,
                sortingFn: 'alphanumeric',
                enableResizing: true,
                cell: (row) => (
                  <span
                    style={textStyleAbgeschlossen(
                      row.row.original.bearbeitungsstand
                    )}
                  >
                    {row.getValue<string>()}
                  </span>
                )
              },
              {
                id: 'anlageDto',
                header: 'Anlagen',
                accessorKey: 'anlageDto',
                enableHiding: true,
                size: 150,
                minSize: 4,
                enableSorting: false,
                enableResizing: true,
                cell: (row) => (
                  <div
                    style={textStyleAbgeschlossen(
                      row.row.original.bearbeitungsstand
                    )}
                    className={'fontWeightBold'}
                  >
                    {row.getValue<{ count: number; size: string }>().count} (
                    {formatByte(
                      row.getValue<{ count: number; size: string }>().size
                    )}
                    )
                  </div>
                )
              },
              {
                id: 'bearbeiterDto',
                header: 'Bearbeiter:in',
                accessorKey: 'bearbeiterDto',
                enableHiding: true,
                size: 10,
                minSize: 4,
                enableSorting: false,
                enableResizing: true,
                cell: (row) => (
                  <ZuweisungComponent
                    position={'relative'}
                    isSchreibtisch={false}
                    antrag={row.row.original}
                  />
                )
              },
              {
                id: 'organizationalUnitName',
                header: 'Empfänger',
                accessorKey: 'organizationalUnitName',
                enableHiding: true,
                size: 10,
                minSize: 4,
                enableSorting: true,
                enableResizing: true,
                cell: (row) => (
                  <span
                    style={textStyleAbgeschlossen(
                      row.row.original.bearbeitungsstand
                    )}
                  >
                    {row.getValue<string>()}
                  </span>
                )
              },
              {
                id: 'bearbeitungsstand',
                header: 'Bearbeitungsstand',
                accessorKey: 'bearbeitungsstand',
                enableHiding: true,
                size: 10,
                minSize: 4,
                enableSorting: true,
                enableResizing: true,
                cell: (row) => bearbeitungsstandCell(row.row.original)
              },
              {
                id: 'bescheidstatus',
                header: 'Bescheidstatus',
                accessorKey: 'bescheidstatus',
                enableHiding: true,
                size: 10,
                minSize: 4,
                enableSorting: true,
                enableResizing: true,
                cell: (row) => bescheidstatusCell(row.row.original)
              },
              {
                id: 'antragsArt',
                header: 'Rückkanal',
                accessorKey: 'antragsArt',
                enableHiding: true,
                size: 4,
                minSize: 2,
                enableSorting: false,
                enableResizing: false,
                cell: (row) => rueckkanalCell(row.row.original)
              }
            ]}
            isDataLoading={isLoading}
            data={data?.items ?? []}
            labels={{
              currentPage: 'Aktuelle Seite',
              detailButton: 'Weitere Infos ein-/ausblenden',
              emptyData: 'Keine Einträge',
              nextPage: 'Nächste Seite',
              of: 'Seite {0} von {1}',
              page: 'Seite',
              pageSize: '{0} Einträge pro Seite',
              previousPage: 'Vorherige Seite',
              rows: 'Zeilen',
              tableTitle: '',
              ascBtn: 'Aufsteigend sortieren',
              descBtn: 'Absteigend sortieren',
              hideBtn: 'Spalte ausblenden'
            }}
            manualPagination={true}
            dataKey="caseId"
            hiddenColumns={columnVisibility}
            manualSorting={true}
            onPaginationChange={(paginationState) => {
              setPaginationState(paginationState);
            }}
            onColumnSortChange={(id, direction) => {
              if (direction === 'asc') {
                setTableSortOrder({ id, direction: 'ASC' });
              } else if (direction === 'desc') {
                setTableSortOrder({ id, direction: 'DESC' });
              } else {
                setTableSortOrder({ id, direction: undefined });
              }
            }}
            onMultiSelectionChange={(row) => {
              setSelectedRows(row);
            }}
            onColumnVisibilityChange={(state) => {
              setColumnVisibility(state);
            }}
            areColumnsHideable
            areColumnsResizeable
            rowHeight={62}
            multiSelect
            clickableRows={false}
            paginationState={paginationState}
            totalDataCount={data?.total}
          />
        </Stack>
        {selectedRows.length > 0 && (
          <Stack direction="row">
            <Button
              variant="primary-text"
              beforeIcon="new-tab"
              id="antraege-oeffnen"
              data-testid={'antraege-oeffnen'}
              onClick={openSelection}
              size="sm"
            >
              {`Auswahl auf Schreibtisch öffnen (${selectedRows.length})`}
            </Button>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default AntragsuebersichtPage;
